import { STATUS } from "@shared/constants";
import { PatientDetailedChartFilter, PatientFilter, PATIENT_TYPE, PatientDailyChartFilter } from "@shared/interfaces";
import { copyObject } from "@shared/utils";
import { produce } from "immer";
import { ActionType, createReducer } from "typesafe-actions";

import { PATIENT_SCALE_OPTIONS } from "../constants";
import { PatientStateType } from "../interfaces";
import * as actions from "./actions";

type Action = ActionType<typeof actions>;

const defaultFilter: PatientFilter = {
  page: 0,
  limit: 50,
  search: "",
  type: PATIENT_TYPE.ALL,
  status: STATUS.ACTIVE,
};

export const defaultDetailedChartFilter: PatientDetailedChartFilter = {
  from: null,
  to: null,
  scale: PATIENT_SCALE_OPTIONS[4],
  patient_id: null,
  page: 0,
  limit: 100000,
};

export const defaultDailyChartFilter: PatientDailyChartFilter = {
  patient_id: null,
  from: null,
  to: null,
  page: 0,
  limit: 100000,
};

export const initialState: PatientStateType = {
  patient: null,
  patients: [],
  patientsTotal: 0,
  filter: { ...defaultFilter },

  detailedChartFilter: copyObject(defaultDetailedChartFilter),
  detailedChartValues: [],

  dailyChartFilter: copyObject(defaultDailyChartFilter),
  dailyChartValues: [],
  dailyChartNotifications: [],
};

const reducer = createReducer<PatientStateType, Action>(initialState)
  .handleAction(actions.getPatients.success, (state, action) =>
    produce(state, (nextState) => {
      const { rows, count, is_clear } = action.payload;
      nextState.patients = !is_clear ? [...nextState.patients, ...rows] : [...rows];
      nextState.patientsTotal = count;
    }),
  )
  .handleAction(actions.getPatient.success, (state, action) =>
    produce(state, (nextState) => {
      const { patient } = action.payload;
      nextState.patient = patient;
    }),
  )
  .handleAction(actions.setFilter, (state, action) =>
    produce(state, (nextState) => {
      nextState.filter = action.payload || { ...defaultFilter };
    }),
  )
  .handleAction(actions.updateFilter, (state, action) =>
    produce(state, (nextState) => {
      nextState.filter = { ...state.filter, ...action.payload };
    }),
  )
  .handleAction(actions.setDetailedChartFilter, (state, action) =>
    produce(state, (nextState) => {
      nextState.detailedChartFilter = action.payload || { ...defaultDetailedChartFilter };
    }),
  )
  .handleAction(actions.setDailyChartFilter, (state, action) =>
    produce(state, (nextState) => {
      nextState.dailyChartFilter = action.payload || { ...defaultDailyChartFilter };
    }),
  )
  .handleAction(actions.getPatientDetailedSensorsData.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.detailedChartValues = action.payload.rows;
    }),
  )
  .handleAction(actions.getPatientDailyAlertNotifications.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.dailyChartNotifications = action.payload.rows;
    }),
  )
  .handleAction(actions.getPatientSensorsAverageData.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.dailyChartValues = action.payload.rows;
    }),
  )
  .handleAction(actions.clearPatients, (state) =>
    produce(state, (nextState) => {
      nextState.patients = [];
      nextState.patientsTotal = 0;
    }),
  )
  .handleAction(actions.clearPatient, (state) =>
    produce(state, (nextState) => {
      nextState.patient = null;
    }),
  )
  .handleAction(actions.createPatient.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.patients = [action.payload.patient, ...nextState.patients];
    }),
  )
  .handleAction(actions.importPatients.success, (state, action) =>
    produce(state, (nextState) => {
      nextState.importPatientsResults = action.payload;
    }),
  )
  .handleAction(actions.updatePatient.success, (state, action) =>
    produce(state, (nextState) => {
      const { patient } = action.payload;
      const patients = nextState.patients;

      const foundPatientIndex = patients.findIndex((p) => patient.id === p.id);
      patients[foundPatientIndex] = action.payload.patient;

      nextState.patients = [...patients];

      if (state.patient && state.patient.id === patient.id) {
        nextState.patient = patient;
      }
    }),
  )
  .handleAction(actions.updatePatientStatus.success, (state, action) =>
    produce(state, (nextState) => {
      const { patient } = action.payload;
      const patients = nextState.patients;

      const foundPatientIndex = patients.findIndex((p) => patient.id === p.id);
      patients[foundPatientIndex] = patient;

      nextState.patients = [...patients];

      if (state.patient && state.patient.id === patient.id) {
        nextState.patient = patient;
      }
    }),
  );

export { reducer as PatientReducer };
